'use client';

import { useCallback, useState } from 'react';
import type { FieldAttributes, FieldProps } from 'formik';
import { ErrorMessage, Field } from 'formik';
import Turnstile from 'react-turnstile';

export const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
export const turnstileTheme: 'auto' | 'light' | 'dark' = 'light';

export const InputFieldset = ({
  name,
  label,
  ...rest
}: FieldAttributes<{ label: string; autoComplete?: string }>) => (
  <div className="form-control">
    <label htmlFor={name}>
      <span>{label}</span>
      <ErrorMessage name={name} className="form-field-error" component="span" />
    </label>
    <Field id={name} name={name} {...rest} className="form-input" />
  </div>
);

interface TurnstileFieldProps extends FieldProps {
  siteKey?: string;
  action: string;
  theme: 'auto' | 'light' | 'dark';
  widgetKey: number;
  onWidgetError?: (error: unknown) => void;
}

export function TurnstileField({
  siteKey,
  action,
  theme,
  widgetKey,
  onWidgetError,
  form: { touched, errors, setFieldValue, setFieldError, setFieldTouched },
}: TurnstileFieldProps) {
  const fieldErrors = errors as { turnstile?: string };
  const touchedFields = touched as { turnstile?: boolean };

  if (!siteKey) {
    return (
      <div className="form-error">
        Authentication verification is unavailable. Please try again later.
      </div>
    );
  }

  return (
    <div className="h-[78px]">
      <Turnstile
        key={widgetKey}
        sitekey={siteKey}
        theme={theme}
        action={action}
        onVerify={(value) => setFieldValue('turnstile', value)}
        onExpire={() => setFieldValue('turnstile', '')}
        onTimeout={() => {
          setFieldValue('turnstile', '', false);
          setFieldTouched('turnstile', true, false);
          setFieldError('turnstile', 'Verification timed out. Please try again.');
        }}
        onError={(error) => {
          setFieldValue('turnstile', '', false);
          setFieldTouched('turnstile', true, false);
          setFieldError('turnstile', 'Verification could not load. Please refresh and try again.');
          onWidgetError?.(error);
        }}
      />
      {fieldErrors.turnstile && touchedFields.turnstile ? (
        <div className="form-field-error">{fieldErrors.turnstile}</div>
      ) : null}
    </div>
  );
}

export function useTurnstileReset() {
  const [turnstileKey, setTurnstileKey] = useState(0);
  const resetTurnstile = useCallback(() => {
    setTurnstileKey((currentKey) => currentKey + 1);
  }, []);

  return { turnstileKey, resetTurnstile };
}

export function FormIntro({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-6">
      <h2 className="h3 font-merriweather mb-2">{title}</h2>
      <div className="text-sm text-slate-600 space-y-1">{children}</div>
    </div>
  );
}

export function FormSwitchButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="underline text-slate-800 hover:text-slate-600 font-medium cursor-pointer"
    >
      {children}
    </button>
  );
}

export function formErrorMessage(error: { title: string; userMessage: string }) {
  return `${error.title}: ${error.userMessage}`;
}
